import { toast } from 'sonner';
import { platform, isStandalone, isInAppBrowser } from './platform';

// Service-worker registration and install-prompt plumbing.
//
// platform() and isStandalone() live in lib/platform.js because the scanner's
// permission copy needs the same distinctions; re-exported here so callers have
// one obvious import for anything PWA-shaped.
export { platform, isStandalone, isInAppBrowser };

let deferredPrompt = null;
const promptListeners = new Set();

function emitPromptChange() {
  promptListeners.forEach((listener) => listener());
}

export function onInstallAvailabilityChange(listener) {
  promptListeners.add(listener);
  return () => promptListeners.delete(listener);
}

/** True when Chromium has offered us a prompt we have not yet used. */
export function canPromptInstall() {
  return deferredPrompt !== null;
}

/**
 * Show the browser's own install dialog. Returns 'accepted' | 'dismissed' |
 * 'unavailable'. The saved event is single-use, so it is dropped either way.
 */
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const event = deferredPrompt;
  deferredPrompt = null;
  emitPromptChange();
  try {
    event.prompt();
    const choice = await event.userChoice;
    return choice?.outcome === 'accepted' ? 'accepted' : 'dismissed';
  } catch {
    return 'dismissed';
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Chromium fires this instead of showing its own banner; hold it so the
    // install screen can raise the real dialog at a moment that makes sense.
    event.preventDefault();
    deferredPrompt = event;
    emitPromptChange();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emitPromptChange();
  });
}

/**
 * Has the kill switch been thrown? Checked page-side as well as inside the
 * worker: a worker that is broken, or one we are about to replace, cannot be
 * trusted to remove itself.
 */
async function killSwitchEngaged() {
  try {
    const res = await fetch('/sw-kill.json', { cache: 'no-store' });
    if (!res.ok) return false;
    const body = await res.json();
    return body?.kill === true;
  } catch {
    // Can't reach it — assume normal operation rather than tearing out a
    // working worker because the member is on a bad connection.
    return false;
  }
}

async function removeServiceWorkers() {
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
  const names = await caches.keys();
  await Promise.all(
    names.filter((name) => name.startsWith('ihsanone-')).map((name) => caches.delete(name)),
  );
}

/**
 * Register the worker. Production only — in development CRA's dev server and a
 * caching worker fight each other, and the bugs that produces are not real.
 */
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production') return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    // Honour the kill switch before registering, or we would immediately put
    // back the worker that was just told to remove itself.
    if (await killSwitchEngaged()) {
      await removeServiceWorkers().catch(() => {});
      return;
    }

    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // A worker that reaches "installed" while another controls the page
            // is a new version waiting. Never reload without asking — a member
            // could be mid-form.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              toast('New version available', {
                duration: Infinity,
                action: {
                  label: 'Refresh',
                  onClick: () => {
                    installing.postMessage('SKIP_WAITING');
                  },
                },
              });
            }
          });
        });
      })
      .catch(() => {
        /* registration failure must never break the app */
      });

    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}
